import { ContactModel } from '../../../contact/models/contact.model';
import { PreventiveTreatmentCategory, PreventiveTreatmentType } from './medical.enums';
import { PREVENTIVE_TREATMENT_TYPES, PreventiveTreatmentTypeDefinition } from './preventive-treatment-types.const';

/** Gewicht: gespeichert in Gramm, angezeigt in kg. */
export function gramsToKg(grams: number | null | undefined): number | null {
  if (grams === null || grams === undefined || Number.isNaN(grams)) return null;
  return grams / 1000;
}

export function kgToGrams(kg: number | null | undefined): number | null {
  if (kg === null || kg === undefined || Number.isNaN(kg)) return null;
  return Math.round(kg * 1000);
}

/** Anzeigename eines Tierarztes: Praxis über `companyName`, sonst Titel, Vor- und Nachname. */
export function formatVeterinarianName(
  contact: Pick<ContactModel, 'companyName' | 'title' | 'firstName' | 'lastName'> | null | undefined
): string {
  if (!contact) return '';
  const company = contact.companyName?.trim();
  if (company) return company;
  return [contact.title, contact.firstName, contact.lastName]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
}

/** Erreger bzw. Wirkstoffe, die zu Kategorie und Tierart passen. `other` ist immer dabei. */
export function typesForCategoryAndSpecies(
  category: PreventiveTreatmentCategory | null | undefined,
  animalCategory: string | null | undefined
): PreventiveTreatmentTypeDefinition[] {
  return PREVENTIVE_TREATMENT_TYPES.filter(def => {
    if (def.value === PreventiveTreatmentType.OTHER) return true;
    if (!category || def.category !== category) return false;
    return def.species === null || (!!animalCategory && def.species.includes(animalCategory));
  });
}

/** Kürzestes definiertes Intervall der gewählten Erreger in Monaten, `null` wenn keins definiert ist. */
export function shortestIntervalMonths(types: (PreventiveTreatmentType | string)[] | null | undefined): number | null {
  const intervals = (types ?? [])
    .map(type => PREVENTIVE_TREATMENT_TYPES.find(def => def.value === type)?.intervalMonths ?? null)
    .filter((months): months is number => months !== null);
  return intervals.length ? Math.min(...intervals) : null;
}

/** Vorschlag für die nächste Fälligkeit: Datum plus kürzestes Intervall, `null` ohne Intervall. */
export function suggestNextDue(
  date: Date | null | undefined,
  types: (PreventiveTreatmentType | string)[] | null | undefined
): Date | null {
  if (!date) return null;
  const months = shortestIntervalMonths(types);
  return months === null ? null : addMonths(date, months);
}

/** Addiert Monate und bleibt am Monatsende (31.01. + 1 Monat = 28./29.02.). */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(date.getDate(), lastDay));
  return result;
}

/** Lokales Datum als `YYYY-MM-DD` (für reine Datumsfelder des Backends). */
export function toIsoDateString(date: Date | null | undefined): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Liest den Datumsteil eines Backend-Werts (`2026-08-01` oder `2026-08-01T00:00:00+02:00`)
 * als lokales Datum um Mitternacht. So verschiebt keine Zeitzone den Tag.
 */
export function parseIsoDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Datumsteil (`YYYY-MM-DD`) eines Backend-Werts, für Vergleiche und Sortierung. */
export function isoDatePart(value: string | null | undefined): string | null {
  return value ? value.slice(0, 10) : null;
}

export function todayIsoDate(): string {
  return toIsoDateString(new Date())!;
}

/** Tage von `from` bis `to` (beide nur Datumsteil). */
export function daysBetween(from: string | Date, to: string | Date): number {
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}
